import { NavLink } from "react-router-dom";
import { hasCapability, type BrokerIdentity } from "@/lib/brokerAuth";
import { useProductLine } from "@/platform/ProductLineProvider";
import { PRODUCT_LINE_NAV } from "@/platform/productLine";
import { useSupportChat } from "@/components/SupportChatProvider";

// The left-nav contents: the broker's identity (avatar + name + brokerage) above
// the primary destinations, then a "Need help?" support card. Rendered once by
// PortalShell — as the persistent sidebar at lg+ and inside the off-canvas drawer
// below lg — so there's a single source of the nav (DRY). The destination list
// is driven by the active platform (Commercial | Surety) via PRODUCT_LINE_NAV,
// so switching the product line reskins the nav in place. Real destinations are
// gated off capabilities, never the raw role string. Every row WITHOUT a route —
// the shared bottom items (Documents, Commissions, Support) and the destinations
// a platform hasn't wired yet — opens live chat when it is configured, so a dead
// control becomes "nothing here yet, ask us" (BRK-S136). So does the "Need help?"
// card. The destination list is the scroll region and the card sits below it,
// so the card stays in view however long the list is (BRK-S229).
interface SideNavProps {
  identity: BrokerIdentity | null;
  /** Resolved broker name (falls back to the email in PortalShell). */
  displayName: string;
  /** Brokerage name shown under the broker name. */
  brokerageName: string;
  /** Called when a destination is chosen — lets the mobile drawer close itself. */
  onNavigate?: () => void;
}

const linkBase =
  "flex items-center gap-3 rounded-r-md border-l-[3px] border-transparent px-3 py-2 text-sm font-medium transition-colors";
const inactive = "text-brk-text hover:bg-brk-bg";

function navClass({ isActive }: { isActive: boolean }): string {
  return `${linkBase} ${
    isActive ? "border-brk-accent bg-brk-accent/10 text-brk-accent" : inactive
  }`;
}

function initialsFrom(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

// Icons kept tiny + local so the nav is a single self-contained file.
const icons = {
  exchange: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" fill="currentColor" />
      <rect
        x="14"
        y="3"
        width="7"
        height="7"
        rx="1.5"
        fill="currentColor"
        opacity="0.5"
      />
      <rect
        x="3"
        y="14"
        width="7"
        height="7"
        rx="1.5"
        fill="currentColor"
        opacity="0.5"
      />
      <rect x="14" y="14" width="7" height="7" rx="1.5" fill="currentColor" />
    </>
  ),
  directMarkets: (
    <path
      d="M3 9l1.6-4.4A1 1 0 0 1 5.5 4h13a1 1 0 0 1 .9.6L21 9M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5M3 9h18M9.5 20v-5.5h5V20"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  accounts: (
    <path
      d="M4 20V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14M4 20h16M8 8h4M8 12h4M8 16h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  submissions: (
    <path
      d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm7 0v5h5M9 13h6M9 17h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  quotes: (
    <path
      d="M12.6 3H6a2 2 0 0 0-2 2v6.6a2 2 0 0 0 .6 1.4l6.4 6.4a2 2 0 0 0 2.8 0l6-6a2 2 0 0 0 0-2.8L13.4 3.4A2 2 0 0 0 12.6 3ZM8 8h.01"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  binders: (
    <path
      d="M6 3h11a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2 0v18M11 8h4M11 12h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  policies: (
    <path
      d="M12 3 5 6v5c0 4.4 3 7.2 7 8.5 4-1.3 7-4.1 7-8.5V6l-7-3Zm-2.2 8.2 1.6 1.6 3.2-3.4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  declinations: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M6 6l12 12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
  // Surety platform icons.
  construction: (
    <path
      d="M4 21h16M5 21v-6a7 7 0 0 1 14 0v6M9.5 4.2 5 8m9.5-3.8L19 8M12 3v1.5m-2.5 3.7 5-1.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  commercialBond: (
    <path
      d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6M4 10h16"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  bondLibrary: (
    <path
      d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19a1 1 0 0 1 1 1v14.5M6.5 17H20M6.5 17A1.5 1.5 0 0 0 5 18.5 1.5 1.5 0 0 0 6.5 20H19M9 7h7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  bonds: (
    <path
      d="M4 5h13a1 1 0 0 1 1 1v13a1 1 0 0 1-1.5.87L14 18l-2.5 1.9L9 18l-2.5 1.9L4 18V5Zm4 4h8M8 12h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  obligees: (
    <path
      d="M3 20a5 5 0 0 1 10 0M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm8 4a3 3 0 1 0 0 .01M15 20a5 5 0 0 1 6-4.9"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  claims: (
    <path
      d="M12 3.5 3.8 6.2v5.1c0 4.6 3.3 7.6 8.2 9.2 4.9-1.6 8.2-4.6 8.2-9.2V6.2L12 3.5ZM12 8.3v4.2M12 15.6h.01"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  documents: (
    <path
      d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  commissions: (
    <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M14.5 9.2c-.5-.9-1.5-1.4-2.6-1.4-1.5 0-2.6.9-2.6 2.1 0 2.9 5.4 1.6 5.4 4.4 0 1.2-1.2 2.1-2.8 2.1-1.2 0-2.3-.6-2.8-1.5M12 6.2v1.6m0 8.4v1.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  support: (
    <path
      d="M4 13a8 8 0 0 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 1Zm16 0v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
};

function NavIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      {children}
    </svg>
  );
}

// Bottom-of-nav items, shared by both platforms. None has a route: Documents and
// Commissions aren't wired yet, and Support is an action rather than a
// destination. Icons are named like NavItemConfig's so these render through the
// same InertNavItem as an unwired platform destination.
const SCAFFOLD: { label: string; icon: string }[] = [
  { label: "Documents", icon: "documents" },
  { label: "Commissions", icon: "commissions" },
  { label: "Support", icon: "support" },
];

/**
 * A nav row with no route — presentational scaffolding, or a destination this
 * platform hasn't wired yet. Both used to render this markup separately; they
 * now share it, because they also share the behaviour.
 *
 * `onOpenChat` present ⇒ the row opens live chat, and it must NOT be
 * `aria-disabled`: announcing a control as unavailable while it opens a chat
 * window is worse than either behaviour on its own. Absent (chat unconfigured)
 * ⇒ the row is genuinely inert, and says so.
 */
function InertNavItem({
  label,
  icon,
  onOpenChat,
}: {
  label: string;
  icon: string;
  onOpenChat?: () => void;
}) {
  return (
    <button
      type="button"
      aria-disabled={onOpenChat ? undefined : "true"}
      onClick={onOpenChat}
      className={`${linkBase} ${inactive} text-left`}
    >
      <NavIcon>{icons[icon as keyof typeof icons]}</NavIcon>
      {label}
    </button>
  );
}

export function SideNav({
  identity,
  displayName,
  brokerageName,
  onNavigate,
}: SideNavProps) {
  const { productLine } = useProductLine();
  const { enabled: chatEnabled, open: openChat } = useSupportChat();
  // Destinations for the active platform, with capability-gated items filtered
  // out (e.g. Accounts requires account:view).
  const items = PRODUCT_LINE_NAV[productLine].filter(
    (item) => !item.capability || hasCapability(identity, item.capability),
  );

  return (
    <div className="flex h-full flex-col gap-6 p-4 text-brk-text">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brk-bg text-sm font-semibold text-brk-dim ring-1 ring-brk-border"
        >
          {initialsFrom(displayName)}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-brk-text">
            {displayName}
          </span>
          <span className="truncate text-xs text-brk-dim">{brokerageName}</span>
        </span>
      </div>

      <nav
        aria-label="Primary"
        className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1"
      >
        {items.map((item) =>
          item.inert || !item.to ? (
            <InertNavItem
              key={item.label}
              label={item.label}
              icon={item.icon}
              onOpenChat={chatEnabled ? openChat : undefined}
            />
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={navClass}
              onClick={onNavigate}
            >
              <NavIcon>{icons[item.icon as keyof typeof icons]}</NavIcon>
              {item.label}
            </NavLink>
          ),
        )}

        {SCAFFOLD.map((item) => (
          <InertNavItem
            key={item.label}
            label={item.label}
            icon={item.icon}
            onOpenChat={chatEnabled ? openChat : undefined}
          />
        ))}
      </nav>

      {/* Support card — opens live chat when configured, else inert. */}
      <button
        type="button"
        onClick={chatEnabled ? openChat : undefined}
        className="flex shrink-0 items-center gap-3 rounded-xl border border-brk-border bg-brk-panel p-3 text-left transition-colors hover:bg-brk-bg"
      >
        <span
          aria-hidden="true"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brk-accent/10 text-brk-accent"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              strokeWidth="1.7"
            />
            <path
              d="M9.5 9.5a2.5 2.5 0 0 1 4.6 1.2c0 1.7-2.1 1.8-2.1 3M12 16.5h.01"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-sm font-semibold text-brk-text">
            Need help?
          </span>
          <span className="text-xs text-brk-dim">Contact Support</span>
        </span>
        <span aria-hidden="true" className="text-brk-dim">
          ›
        </span>
      </button>
    </div>
  );
}
